import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sparkles, Building2, Ruler } from "lucide-react";
import { useNavigate } from "react-router-dom";

interface Recommendation {
  robots: string[];
  description: string;
}

const RobotRecommender = () => {
  const navigate = useNavigate();
  const [businessType, setBusinessType] = useState("");
  const [facilitySize, setFacilitySize] = useState("");
  const [recommendation, setRecommendation] = useState<Recommendation | null>(null);

  const getRecommendation = (industry: string, size: number): Recommendation => {
    // Convert to square meters for comparison
    const sizeM2 = size;

    // Recommendation logic based on the chart
    const recommendations: Record<string, (size: number) => string[]> = {
      retail: (s) => {
        if (s <= 1000) return ["CC1"];
        if (s <= 5000) return ["CC1 Pro", "MT1 VAC"];
        if (s <= 30000) return ["CC1 Pro", "MT1 VAC"];
        return ["CC1 Pro", "MT1 VAC"];
      },
      healthcare: (s) => {
        if (s <= 500) return ["CC1 Pro", "MT1 VAC"];
        if (s <= 3000) return ["CC1 Pro", "MT1 VAC", "MT1"];
        if (s <= 10000) return ["CC1 Pro", "MT1 VAC"];
        return ["CC1 Pro", "MT1 VAC"];
      },
      hospitality: (s) => {
        if (s <= 5000) return ["CC1"];
        if (s <= 10000) return ["CC1 Pro", "MT1 MAX", "MT1 VAC"];
        return ["CC1 Pro", "MT1 MAX", "MT1 VAC"];
      },
      education: (s) => {
        if (s <= 1000) return ["CC1 Pro", "CC1", "MT1 VAC"];
        if (s <= 5000) return ["University"];
        return ["University"];
      },
      entertainment: (s) => {
        if (s <= 3000) return ["CC1 Pro", "CC1", "MT1 VAC"];
        return ["CC1 Pro", "CC1", "MT1 VAC"];
      },
      transportation: (s) => {
        if (s <= 3000) return ["CC1 Pro", "MT1 VAC"];
        if (s <= 10000) return ["CC1 Pro", "MT1 MAX", "MT1 VAC"];
        return ["CC1 Pro", "MT1 MAX", "MT1 VAC"];
      },
      "office-building": (s) => {
        if (s <= 10000) return ["CC1 Pro", "MT1 VAC"];
        return ["MT1 MAX"];
      },
      "public-service": (s) => {
        if (s <= 3000) return ["CC1 Pro", "MT1 VAC"];
        return ["CC1 Pro", "MT1 VAC"];
      },
      restaurant: (s) => {
        if (s <= 3000) return ["CC1 Pro"];
        return ["CC1 Pro"];
      },
    };

    const robots = recommendations[industry]?.(sizeM2) || ["CC1 Pro"];

    const robotDescriptions: Record<string, string> = {
      "CC1": "The CC1 is ideal for small to medium spaces with basic cleaning needs. Perfect for retail stores and small offices.",
      "CC1 Pro": "Our most versatile cleaning robot, the CC1 Pro handles a wide range of environments from restaurants to hospitals with advanced navigation and extended runtime.",
      "MT1": "The MT1 offers powerful cleaning for medium-sized facilities with enhanced dust collection and superior efficiency.",
      "MT1 VAC": "The MT1 VAC provides comprehensive vacuuming and mopping capabilities for medium to large spaces, ideal for healthcare and retail environments.",
      "MT1 MAX": "Our heavy-duty solution, the MT1 MAX is designed for large-scale operations like airports, hotels, and parking facilities with maximum coverage and capacity.",
      "University": "Specialized solution for educational campuses with large common areas, multiple surfaces, and high foot traffic.",
    };

    const description = robots.length === 1
      ? robotDescriptions[robots[0]]
      : `Based on your facility, we recommend a combination of: ${robots.join(", ")}. This setup ensures comprehensive coverage and optimal cleaning efficiency for your ${industry} facility.`;

    return { robots, description };
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!businessType || !facilitySize) return;

    const size = parseFloat(facilitySize);
    if (isNaN(size) || size <= 0) return;

    const result = getRecommendation(businessType, size);
    setRecommendation(result);
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <header className="border-b border-[hsl(var(--line))] glass-effect sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button onClick={() => navigate("/")} className="flex items-center gap-3">
              <div className="relative">
                <div className="absolute inset-0 bg-primary/20 blur-xl rounded-full"></div>
                <div className="relative bg-primary text-primary-foreground rounded-full w-10 h-10 flex items-center justify-center font-orbitron font-bold">
                  N
                </div>
              </div>
              <span className="font-orbitron font-bold text-xl">Neil AI</span>
            </button>
            <Button onClick={() => navigate("/")} variant="ghost">
              Back to Home
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto">
          {/* Hero Section */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-primary/10 border border-primary/20 rounded-full px-4 py-2 mb-6">
              <Sparkles className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium text-primary">AI-Powered Recommendations</span>
            </div>
            <h1 className="font-orbitron font-bold text-4xl md:text-5xl mb-4">
              Find Your Perfect <span className="text-primary">Robot Solution</span>
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Tell us about your business, and we'll recommend the ideal automation solution tailored to your needs.
            </p>
          </div>

          {/* Form Card */}
          <Card className="border-[hsl(var(--line))] glass-effect p-8 mb-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Business Type */}
              <div className="space-y-2">
                <Label htmlFor="business-type" className="flex items-center gap-2 text-base">
                  <Building2 className="w-5 h-5 text-primary" />
                  Business Type / Industry
                </Label>
                <Select value={businessType} onValueChange={setBusinessType}>
                  <SelectTrigger id="business-type" className="w-full">
                    <SelectValue placeholder="Select your industry" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="retail">Retail</SelectItem>
                    <SelectItem value="healthcare">Healthcare</SelectItem>
                    <SelectItem value="hospitality">Hospitality</SelectItem>
                    <SelectItem value="education">Education</SelectItem>
                    <SelectItem value="entertainment">Entertainment & Sport</SelectItem>
                    <SelectItem value="transportation">Transportation</SelectItem>
                    <SelectItem value="office-building">Residential & Office Building</SelectItem>
                    <SelectItem value="public-service">Public Service</SelectItem>
                    <SelectItem value="restaurant">Restaurant</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Facility Size */}
              <div className="space-y-2">
                <Label htmlFor="facility-size" className="flex items-center gap-2 text-base">
                  <Ruler className="w-5 h-5 text-primary" />
                  Facility Size (square meters)
                </Label>
                <Input
                  id="facility-size"
                  type="number"
                  placeholder="e.g., 5000"
                  value={facilitySize}
                  onChange={(e) => setFacilitySize(e.target.value)}
                  min="1"
                  step="1"
                />
                <p className="text-xs text-muted-foreground">
                  Enter the approximate size of your facility in square meters
                </p>
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                className="w-full"
                size="lg"
                disabled={!businessType || !facilitySize}
              >
                <Sparkles className="w-4 h-4 mr-2" />
                Get Recommendation
              </Button>
            </form>
          </Card>

          {/* Recommendation Result */}
          {recommendation && (
            <Card className="border-primary/30 bg-primary/5 p-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/20 mb-4">
                  <Sparkles className="w-8 h-8 text-primary" />
                </div>
                <h2 className="font-orbitron font-bold text-2xl mb-2">
                  Your Recommended Solution
                </h2>
              </div>

              <div className="space-y-6">
                {/* Robot Models */}
                <div>
                  <h3 className="font-semibold text-lg mb-3">Recommended Robot Model{recommendation.robots.length > 1 ? 's' : ''}:</h3>
                  <div className="flex flex-wrap gap-2">
                    {recommendation.robots.map((robot) => (
                      <div
                        key={robot}
                        className="bg-primary text-primary-foreground px-4 py-2 rounded-full font-orbitron font-bold"
                      >
                        {robot}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Description */}
                <div>
                  <h3 className="font-semibold text-lg mb-3">Why This Solution:</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {recommendation.description}
                  </p>
                </div>

                {/* CTA Buttons */}
                <div className="flex flex-col sm:flex-row gap-4 pt-4">
                  <Button
                    onClick={() => navigate("/robots")}
                    className="flex-1"
                    size="lg"
                  >
                    View All Robots
                  </Button>
                  <Button
                    onClick={() => navigate("/#contact")}
                    variant="secondary"
                    className="flex-1 glow-gold"
                    size="lg"
                  >
                    Book a Demo
                  </Button>
                </div>
              </div>
            </Card>
          )}
        </div>
      </main>
    </div>
  );
};

export default RobotRecommender;
